import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_STEPS = [
  {
    element: '[data-testid="dashboard"]',
    popover: {
      title: 'Welcome to RootTrace',
      description: 'This is your command centre. The Dashboard shows live KPIs, pending incident reviews, unassigned investigations, and equipment alerts at a glance.',
      side: 'bottom', align: 'center',
    },
  },
  {
    element: '[data-testid="global-search-trigger"]',
    popover: {
      title: 'Global Search',
      description: 'Press Cmd+K (or Ctrl+K) to instantly search across investigations, incidents, tasks, evidence, inventory, and more.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-notifications"]',
    popover: {
      title: 'Incident Notifications',
      description: 'All reported incidents appear here. You can review, approve, or reject them. Approved incidents automatically create a new investigation.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-investigations"]',
    popover: {
      title: 'Investigations',
      description: 'View and manage all active investigations. Assign investigators, generate ICAO tasks, and track progress from initiation to final report.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-tasks"]',
    popover: {
      title: 'Tasks',
      description: 'All investigation tasks across the 14 ICAO specialist categories. Update status, record findings, and track assignments.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-evidence"]',
    popover: {
      title: 'Evidence Management',
      description: 'Upload and manage evidence files — photos, documents, audio, video. Each piece links to its investigation with a chain-of-custody trail.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-inventory"]',
    popover: {
      title: 'Equipment Inventory',
      description: 'Track investigation equipment — assign items to cases, print QR labels, monitor calibration dates, and manage check-in/check-out.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-ar-identify"]',
    popover: {
      title: 'AR Parts Identification',
      description: 'Use your camera or upload photos of wreckage. AI-powered vision analysis identifies aircraft or vessel parts and links them as evidence.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-reports"]',
    popover: {
      title: 'Reports',
      description: 'Generate final investigation reports with auto-populated sections, table of contents, and IIC approval workflows. Export to PDF or Word.',
      side: 'right', align: 'start',
    },
  },
  {
    element: '[data-testid="nav-settings"]',
    popover: {
      title: 'Settings & Administration',
      description: 'Manage users, configure email, run data backups, and review login audit trails. Admin-only features are gated by role.',
      side: 'right', align: 'start',
    },
  },
];

let cachedVoice = null;
let voicesLoaded = false;

function loadVoices() {
  return new Promise((resolve) => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { resolve(voices); return; }
    window.speechSynthesis.addEventListener('voiceschanged', () => {
      resolve(window.speechSynthesis.getVoices());
    }, { once: true });
  });
}

function pickVoice(voices) {
  const byName = (n) => voices.find(v => v.name === n);
  const msMatch = voices.find(v => v.lang === 'en-US' && /Aria|Jenny|Guy|Zira/i.test(v.name));
  return byName('Google US English')
    || msMatch
    || byName('Samantha')
    || voices.find(v => v.lang === 'en-US')
    || voices[0]
    || null;
}

async function speak(text, muted) {
  if (muted || typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  if (!voicesLoaded) {
    const voices = await loadVoices();
    cachedVoice = pickVoice(voices);
    voicesLoaded = true;
  }
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-US';
  utterance.rate = 0.95;
  utterance.pitch = 1;
  if (cachedVoice) utterance.voice = cachedVoice;
  window.speechSynthesis.speak(utterance);
}

export function useTour() {
  const driverRef = useRef(null);
  const mutedRef = useRef(localStorage.getItem('rt_tour_muted') === 'true');

  const startTour = useCallback(() => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch {}
    }

    const hasSpeech = typeof window !== 'undefined' && !!window.speechSynthesis;
    // Pre-load voices
    if (hasSpeech) window.speechSynthesis.getVoices();

    const muteBtn = (muted) => hasSpeech
      ? `<button onclick="document.dispatchEvent(new CustomEvent('rt-tour-mute'))" style="background:none;border:none;cursor:pointer;font-size:16px;padding:2px 6px;border-radius:4px;margin-left:6px;opacity:0.7" title="${muted ? 'Unmute' : 'Mute'} voice">${muted ? '🔇' : '🔊'}</button>`
      : '';

    const d = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(15, 23, 42, 0.75)',
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: 'rt-tour-popover',
      steps: TOUR_STEPS.map(step => ({
        ...step,
        popover: {
          ...step.popover,
          description: step.popover.description + muteBtn(mutedRef.current),
        },
      })),
      onHighlightStarted: (el, step) => {
        const idx = TOUR_STEPS.findIndex(s => s.popover.title === step.popover.title);
        if (idx >= 0) {
          const s = TOUR_STEPS[idx];
          speak(`${s.popover.title}. ${s.popover.description}`, mutedRef.current);
        }
      },
      onDestroyStarted: () => {
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        localStorage.setItem('rt_tour_completed', 'true');
        d.destroy();
      },
    });

    // Mute toggle handler
    const muteHandler = () => {
      mutedRef.current = !mutedRef.current;
      localStorage.setItem('rt_tour_muted', String(mutedRef.current));
      if (mutedRef.current && window.speechSynthesis) window.speechSynthesis.cancel();
    };
    document.addEventListener('rt-tour-mute', muteHandler);

    driverRef.current = d;
    d.drive();

    return () => {
      document.removeEventListener('rt-tour-mute', muteHandler);
    };
  }, []);

  // Auto-start on first login
  useEffect(() => {
    const completed = localStorage.getItem('rt_tour_completed');
    if (!completed) {
      const timer = setTimeout(() => startTour(), 1500);
      return () => clearTimeout(timer);
    }
  }, [startTour]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch {}
      }
    };
  }, []);

  return { startTour };
}

export { TOUR_STEPS };
